import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Grid } from '@material-ui/core';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import TaskAttachmentCard from '../../tasks/cards/TaskAttachmentCard';
import { Task } from '../../tasks/Task.interface';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import TestStepCard from '../cards/TestStepCard';
import VerificationDesignInputReferenceCard from '../cards/VerificationDesignInputReferenceCard';
import VerificationDesignOutputReferenceCard from '../cards/VerificationDesignOutputReferenceCard';
import VerificationInfoCard from '../cards/VerificationInfoCard';
import VerificationUserNeedReferenceCard from '../cards/VerificationUserNeedReferenceCard';
import {
  useFindProduct,
  useFindProductVerifications,
  useProductFilterQueryParams
} from '../Product.hooks';
import {
  BaseProductVerificationCardProps,
  ProductEntity,
  ProductVerification
} from '../Product.interface';
import ProductDocumentCommonPage from './ProductDocumentCommonPage';
import { getProductEntityName } from '../Product.config';

const ProductVerificationDocumentPage: React.FC = () => {
  const { productId: paramProductId = '' } = useParams<{
    productId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const fetchUserNeedsRef = useRef<Function | undefined>();
  const fetchDesignInputsRef = useRef<Function | undefined>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [productVerifications, setProductVerifications] = useState<
    ProductVerification[]
  >([]);
  const {
    productVerificationList,
    loadingProductVerificationsError,
    findProductVerifications
  } = useFindProductVerifications(productId);
  const { product } = useFindProduct(productId, true);
  const { getParams } = useProductFilterQueryParams();

  const createCardProps = (
    productEntity: ProductEntity
  ): BaseProductVerificationCardProps => {
    const verification: ProductVerification = productEntity as ProductVerification;
    return {
      productId,
      productVerification: verification,
      onUpdate: () => setDidUpdate(true),
      onError: (error: FetchError) => setError(error),
      onChange: (newProductVerification: Partial<ProductVerification>) => {
        const selectedIndex: number = productVerifications.findIndex(
          (un: ProductVerification) => un.id === verification.id
        );
        if (selectedIndex > -1) {
          const verifications: ProductVerification[] = [
            ...productVerifications
          ];
          verifications[selectedIndex] = {
            ...verification,
            ...newProductVerification
          };
          setProductVerifications(verifications);
        }
      }
    };
  };

  useEffect(() => {
    findProductVerifications({ search, ...getParams() });
  }, [findProductVerifications, search, getParams]);

  useEffect(() => {
    if (productVerificationList) {
      setProductVerifications(productVerificationList.data);
    }
  }, [productVerificationList]);

  useEffect(() => {
    setError(loadingProductVerificationsError);
  }, [loadingProductVerificationsError]);

  return (
    <>
      <ProductDocumentCommonPage
        product={product}
        productEntities={productVerifications}
        entity={ProductBoardColumnEntity.Verification}
        renderInfoCard={(
          productEntity: ProductEntity,
          title: string,
          hidden?: boolean
        ) => {
          const props: BaseProductVerificationCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <VerificationInfoCard
                {...createCardProps(productEntity)}
                title={title}
                hidden={hidden}
                enableTitleRedirect
              />
              <TestStepCard
                verificationId={productEntity.id}
                hidden={hidden}
                onError={props.onError}
                onUpdate={props.onUpdate}
              />
            </>
          );
        }}
        renderGridCards={(productEntity: ProductEntity) => {
          const props: BaseProductVerificationCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <Grid item xs={12}>
                <VerificationUserNeedReferenceCard
                  {...props}
                  fetchRef={fetchUserNeedsRef}
                />
              </Grid>
              <Grid item xs={12}>
                <VerificationDesignInputReferenceCard
                  {...props}
                  fetchRef={fetchDesignInputsRef}
                />
              </Grid>
              <Grid item xs={12}>
                <VerificationDesignOutputReferenceCard
                  {...props}
                  onUpdate={() => {
                    fetchUserNeedsRef.current?.();
                    fetchDesignInputsRef.current?.();
                    props.onUpdate?.();
                  }}
                />
              </Grid>
              {productEntity.task && (
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    compact
                    task={productEntity.task}
                    onError={props.onError}
                    onChange={(newTask: Partial<Task>) => {
                      Object.assign(productEntity.task, newTask);
                      // update with new task
                      props.onChange?.({});
                      props.onUpdate?.();
                    }}
                  />
                </Grid>
              )}
            </>
          );
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('verification')} details updated.
      </AppSnackbar>
    </>
  );
};

export default ProductVerificationDocumentPage;
